import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAllPosts } from "../../utils/posts";
import { format } from "date-fns";

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return format(d, "dd MMM yyyy");
}

export default function AdminDashboard() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAllPosts()
      .then(setPosts)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const published = posts.filter(p => p.status === "published").length;
  const drafts = posts.filter(p => p.status === "draft").length;
  const featured = posts.filter(p => p.featured).length;
  const recent = posts.slice(0, 8);

  const categories = {
    "macro-note": posts.filter(p => p.category === "macro-note").length,
    "weekly-outlook": posts.filter(p => p.category === "weekly-outlook").length,
    "trade-idea": posts.filter(p => p.category === "trade-idea").length,
    "education": posts.filter(p => p.category === "education").length,
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 40, flexWrap: "wrap", gap: 16 }}>
        <div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 8 }}>
            Admin Panel
          </div>
          <h2 style={{ fontSize: "1.8rem" }}>Dashboard</h2>
        </div>
        <Link to="/admin/posts/new" className="btn btn-gold">+ New Post</Link>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 40 }}>
        {[
          { val: posts.length, label: "Total Posts" },
          { val: published, label: "Published", color: "#4ade80" },
          { val: drafts, label: "Drafts", color: "#818cf8" },
          { val: featured, label: "Featured", color: "var(--gold)" },
        ].map(s => (
          <div key={s.label} style={{
            padding: "24px",
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius)",
          }}>
            <div style={{ fontFamily: "var(--font-serif)", fontSize: "2.2rem", color: s.color || "var(--text-primary)", lineHeight: 1, marginBottom: 8 }}>
              {loading ? "—" : s.val}
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
        {/* Recent posts table */}
        <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)" }}>
          <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "11px", letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-secondary)" }}>Recent Posts</span>
            <Link to="/admin/posts" style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--gold)" }}>View All →</Link>
          </div>

          {loading ? (
            <div style={{ padding: 24 }}>
              {[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ height: 44, marginBottom: 8 }} />)}
            </div>
          ) : recent.length === 0 ? (
            <div style={{ padding: "40px 24px", textAlign: "center", color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: "12px" }}>
              No posts yet. <Link to="/admin/posts/new" style={{ color: "var(--gold)" }}>Create one →</Link>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--bg-secondary)" }}>
                  {["Title", "Category", "Status", "Date", ""].map(h => (
                    <th key={h} style={{
                      padding: "10px 16px",
                      textAlign: "left",
                      fontFamily: "var(--font-mono)",
                      fontSize: "9px",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: "var(--text-muted)",
                      fontWeight: 400,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recent.map(post => (
                  <tr key={post.id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ fontSize: "13px", color: "var(--text-primary)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {post.title}
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <span className="badge badge-outline" style={{ fontSize: "9px" }}>
                        {post.category?.replace(/-/g, " ")}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <span className={`badge ${post.status === "published" ? "badge-green" : "badge-draft"}`} style={{ fontSize: "9px" }}>
                        {post.status}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)" }}>
                      {formatDate(post.createdAt)}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <Link to={`/admin/posts/${post.id}/edit`} style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--gold)", letterSpacing: "0.1em" }}>
                        EDIT
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Category breakdown */}
        <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "24px" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-secondary)", marginBottom: 20 }}>
            By Category
          </div>
          {Object.entries(categories).map(([cat, count]) => (
            <div key={cat} style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {cat.replace(/-/g, " ")}
                </span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--gold)" }}>{count}</span>
              </div>
              <div style={{ height: 3, background: "var(--border)", borderRadius: 2 }}>
                <div style={{
                  height: "100%",
                  width: posts.length ? `${(count / posts.length) * 100}%` : "0%",
                  background: "var(--gold)",
                  borderRadius: 2,
                  transition: "width 0.6s ease",
                }} />
              </div>
            </div>
          ))}

          <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid var(--border)" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-secondary)", marginBottom: 16 }}>
              Quick Actions
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Link to="/admin/posts/new" className="btn btn-gold" style={{ justifyContent: "center", fontSize: "11px", padding: "10px" }}>
                + New Post
              </Link>
              <Link to="/admin/uploads" className="btn btn-ghost" style={{ justifyContent: "center", fontSize: "11px", padding: "10px" }}>
                Media Library
              </Link>
              <Link to="/" target="_blank" className="btn btn-ghost" style={{ justifyContent: "center", fontSize: "11px", padding: "10px" }}>
                ↗ View Site
              </Link>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          div > div:last-child { grid-template-columns: 1fr !important; }
          div > div:first-child { grid-template-columns: repeat(2,1fr) !important; }
        }
      `}</style>
    </div>
  );
}
